import React, { useCallback, useEffect, useRef, useState } from 'react';
import t from 'react-translate';
import { useSelector } from 'react-redux';
import { Exercise, TemplateSection } from 'redux/schemas/models/exercise';

import { useAppDispatch } from 'redux/store';
import { getCurrentUser } from 'redux/selectors/users';
import { TranslationInfo } from 'redux/schemas/models/edit-translations';
import { closeEditTranslationComponent, translationUpdate } from 'redux/actions/edit-translations';
import { getLectureComponent } from 'redux/selectors/timeline';
import { ComponentType, NLectureComponent } from 'redux/schemas/models/lecture-component';
import { css } from '@emotion/react';
import { doubleSpacing, halfSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import NvIcon from 'shared/components/nv-icon';
import { FileUpload, UploadedFile } from 'redux/schemas/models/file-uploads';
import { Attachment } from 'redux/schemas/models/attachment';
import { AUTOMATED_TRANSLATION_LANGUAGES } from 'automated_translation/components/language-picker';
import EditableTitleWithDescriptionStyled from './base/editable-title-and-description-styled';
import { FileReplacement } from './quiz-base/helper';
import { config } from '@config/config.json';

export const TRANSLATED_FILES_STORAGE_KEY = 'listOfTranslatedFiles';

type EditableProps = {
  editInfo: TranslationInfo,
  containerClass: string,
  onSaveRef: (fn: () => void) => void,
  setSavingDisabled: (isDisabled: boolean) => void,
  setIsLoading: (isLoading: boolean) => void,
};

const EditableAssignment = ({
  editInfo,
  containerClass,
  onSaveRef,
  setSavingDisabled,
  setIsLoading,
}: EditableProps) => {
  const lectureComponent = useSelector((state) => (
    getLectureComponent(state, editInfo.id)
  )) as NLectureComponent<ComponentType.EXERCISE>;
  const exercise = useSelector((state) => state.models.exercises[lectureComponent.exercise]) as Exercise;
  type EditableAssignmentType = {
    activity: {
      title: string;
      description: string;
    };
    sections: TemplateSection[];
    attachments: (Attachment & { file: UploadedFile & { novoedFile?: any } })[];
  };
  const initialStateRef = useRef({
    activity: {
      title: exercise.title,
      description: exercise.description,
    },
    sections: exercise.template?.sections ?? [],
    attachments: exercise.attachments ?? [],
  });
  const initialState = initialStateRef.current;
  const [assignment, setAssignment] = useState<EditableAssignmentType>(initialState);
  const currentUser = useSelector(getCurrentUser);
  const dispatch = useAppDispatch();

  const editableAssignmentStyle = css`
    width: 100%;
    .separator {
      margin: ${standardSpacing}px ${doubleSpacing}px ${standardSpacing}px;
    }
    .sections {
      padding-bottom: ${doubleSpacing}px;
    }
    .attachments {
      margin: 0 40px;
    }
    .file-name {
      display: flex;
      padding: ${standardSpacing}px 0 ${halfSpacing}px;
      .icon {
        padding-right: ${halfSpacing}px;
      }
    }
  `;

  const save = useCallback(() => {
    const attributes = [];
    const attachments = [];
    const fields = Object.keys(assignment.activity);
    fields.forEach(field => {
      if (assignment.activity[field] !== initialState.activity[field]) {
        attributes.push({
          translationId: exercise.translationIds[field],
          name: field,
          text: assignment.activity[field],
          elementType: lectureComponent.type,
          elementId: exercise.id,
        });
      }
    });
    if (assignment.sections !== initialState.sections) {
      assignment.sections.forEach((section, index) => {
        const sectionFields = ['header', 'instructions'];
        sectionFields.forEach(sectionField => {
          if (section[sectionField] !== initialState.sections[index][sectionField]) {
            attributes.push({
              translationId: section.translationIds[sectionField],
              name: sectionField,
              text: section[sectionField],
              elementType: lectureComponent.type,
              elementId: index,
            });
          }
        });
      });
    }
    if (assignment?.attachments !== initialState.attachments) {
      const initialAttachments = initialState.attachments || [];
      assignment.attachments?.forEach((attachment, index) => {
        const initialAttachment = initialAttachments[index];
        if (!initialAttachment || !initialAttachment.file) {
          return;
        }
        if (attachment.file !== initialAttachment.file) {
          const { name, size, type, uniqueId } = attachment.file;
          attachments.push({
            fileName: name,
            fileSize: size,
            fileType: type,
            uniqueId,
            translatedFrom: attachment.id,
          });
        }
      });
    }
    const userLanguage = AUTOMATED_TRANSLATION_LANGUAGES.find((language) => language.isoCode === currentUser.translationPreferenceLanguage);
    const language = config.platformLanguages.find(lang => lang.toUpperCase() === userLanguage?.translationKey);
    const payload = {
      id: lectureComponent.id,
      attributes,
      attachments,
      language,
    };
    setIsLoading(true);
    dispatch(translationUpdate(payload)).then(() => {
      window.dispatchEvent(
        new CustomEvent('translated-files-updated', {
          detail: {
            attachments: attachments.map(a => ({
              translatedFrom: a.translatedFrom,
              exerciseId: lectureComponent.exercise,
            })),
          },
        }),
      );
      dispatch(closeEditTranslationComponent());
    });
  }, [assignment, currentUser.translationPreferenceLanguage, dispatch, editInfo, initialState, exercise, lectureComponent, setIsLoading]);

  const setTranslatedFile = (file, index) => {
    const attachments = [...assignment.attachments];
    const { fileName, fileSize, fileType, uniqueId, novoedFile } = file;
    attachments[index] = {
      ...attachments[index],
      file: {
        name: fileName,
        size: fileSize,
        type: fileType,
        uniqueId,
        novoedFile: {
          ...novoedFile,
          attachmentId: attachments[index].id,
        },
      },
    };
    setAssignment({
      ...assignment,
      attachments,
    });
  };

  useEffect(() => {
    onSaveRef(() => save);
  }, [onSaveRef, save]);

  useEffect(() => {
    if (initialState !== assignment) {
      setSavingDisabled(false);
    }
    return (() => {
      setSavingDisabled(true);
      setIsLoading(false);
    });
  }, [assignment]);

  const setSection = (index, newSection) => {
    const newSections = [...assignment.sections];
    newSections[index] = {
      ...newSections[index],
      header: newSection.title,
      instructions: newSection.description,
    };
    setAssignment({
      ...assignment,
      sections: newSections,
    });
  };

  return (
    <div className='editable-assignment' css={editableAssignmentStyle}>
      <EditableTitleWithDescriptionStyled
        activity={assignment.activity}
        setActivity={(newActivity) => setAssignment({ ...assignment, activity: newActivity })}
        containerClass={containerClass}
        titleName={t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.TITLE()}
        descriptionName={t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.EXPECTATIONS()}
      />
      {
        assignment.attachments?.length > 0 && (
          <React.Fragment>
            <hr className='separator bg-gray-5' />
            <div className='attachments'>
              { t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.ATTACHMENTS.DESCRIPTION() }
              {
                assignment.attachments.map((attachment, index) => {
                  const currentFile = (initialState.attachments[index].file as (UploadedFile & FileUpload));
                  if (!currentFile) {
                    return null;
                  }
                  let translatedFile;
                  const initialTranslatedFiles = localStorage.getItem(TRANSLATED_FILES_STORAGE_KEY) ? JSON.parse(localStorage.getItem(TRANSLATED_FILES_STORAGE_KEY))[currentUser.id] : [];
                  if (initialTranslatedFiles?.includes(attachment.id)) {
                    translatedFile = attachment.autoTranslated;
                  }
                  return (
                    <div className='attachment' key={`attachment-${attachment.id}`}>
                      <div className='file-name'>
                        <NvIcon size='small' icon='files' />
                        { translatedFile?.documentName ?? currentFile.name ?? currentFile.fileName }
                      </div>
                      <FileReplacement setTranslatedFile={(file) => setTranslatedFile(file, index)} fileType={currentFile.type ?? currentFile.contentType} />
                    </div>
                  );
                })
              }
            </div>
          </React.Fragment>
        )
      }
      <div className='sections'>
        {
          assignment.sections.map((section, index) => (
            <div className='section'>
              <hr className='separator bg-gray-5' />
              <EditableTitleWithDescriptionStyled
                activity={{
                  title: section.header,
                  description: section.instructions,
                }}
                setActivity={(newSection) => setSection(index, newSection)}
                containerClass={containerClass}
                titleName={t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.SECTION_HEADING()}
                descriptionName={t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.SECTION_INSTRUCTIONS()}
              />
            </div>
          ))
        }
      </div>
    </div>
  );
};

export default EditableAssignment;
